import {
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CustomerSearchSelector, {
  ClientOption,
} from '../Steps/Components/Selectors/CustomerSearchSelector';
import { useCallback, useState } from 'react';
import { Nullable, PricingSheetRow } from '../../../../shared/types';
import { Box } from '@mui/system';
import { RentalHistorySourceType } from '../../../../shared/types/pricingSheets';
import { addDays, endOfDay, startOfDay, subYears } from 'date-fns';
import { useRentalHistory } from '../../hooks/useRentalHistory';
import _, { isEmpty } from 'lodash';
import { useRentalHistoryJob } from '../../hooks/useRentalHistoryJob';

type EditCustomerDialogType = {
  open: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: (
    rentalHistoryCustomerName: string,
    rentalHistoryCustomerId: string,
    rentalHistoryStartDate: Date,
    rentalHistoryEndDate: Date,
    rentalHistoryCompanyChainId: string,
    rentalHistorySourceType: RentalHistorySourceType,
    pricingSheetItems: any[],
    rentalHistoryCompanyName: string,
  ) => void;
  loading?: boolean;
  companyChains: any;
  pricingSheetRows: PricingSheetRow[];
};

function isValidInteger(str: string) {
  const num = Number(str); // Convert to number
  return Number.isInteger(num) && !isNaN(num) && str.trim() !== ''; // Check if it's an integer
}

export const EditCustomerDialog = ({
  open,
  title,
  onConfirm = () => null,
  onCancel = () => null,
  loading = false,
  pricingSheetRows,
  companyChains,
}: EditCustomerDialogType) => {
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [companyChainId, setCompanyChainId] = useState('NotSelected');
  const [companyChainName, setCompanyChainName] = useState('');
  const [activeRentalHistorySource, setActiveRentalHistorySource] =
    useState(RentalHistorySourceType.Customer);

  const newStartDate = startOfDay(
    addDays(subYears(new Date(), 1), 1),
  );

  const newEndDate = endOfDay(startOfDay(new Date()));
  const id =
    activeRentalHistorySource === RentalHistorySourceType.Customer
      ? customerId
      : companyChainId;
  const enabled = id.length > 0 && isValidInteger(id);

  const {
    data: rentalHistoryCompanyChain = {},
    isFetching: isLoadingRentalHistory,
  } = useRentalHistory(
    activeRentalHistorySource,
    activeRentalHistorySource === RentalHistorySourceType.Customer
      ? customerId
      : companyChainId,
    newStartDate,
    newEndDate,
    enabled &&
      activeRentalHistorySource ===
        RentalHistorySourceType.CompanyChain,
  );

  const {
    data: rentalHistoryCustomer = {},
    loading: isLoadingRentalHistoryJob,
  } = useRentalHistoryJob<any>(
    customerId,
    newStartDate,
    newEndDate,
    enabled &&
      activeRentalHistorySource === RentalHistorySourceType.Customer,
  );

  const formatPricingSheetRows = (rentalHistory: any) => {
    return pricingSheetRows.map((row: PricingSheetRow) => {
      const rentalDaysDay = _.get(
        rentalHistory,
        ['data', row.catClass, 'rentalDaysDay'],
        undefined,
      );
      const rentalDaysMonth = _.get(
        rentalHistory,
        ['data', row.catClass, 'rentalDaysMonth'],
        undefined,
      );
      const rentalProfitDay = _.get(
        rentalHistory,
        ['data', row.catClass, 'totalPriceDay'],
        undefined,
      );
      const rentalProfitMonth = _.get(
        rentalHistory,
        ['data', row.catClass, 'totalPriceMonth'],
        undefined,
      );
      return {
        ...row,
        rentalDaysDay: rentalDaysDay,
        rentalDaysMonth: rentalDaysMonth,
        estimatedYieldDay: Number(
          (row?.dayPrice ?? 0) * (rentalDaysDay ?? 0),
        ),
        estimatedYieldMonth: Number(
          ((row?.monthPrice ?? 0) / 30) * (rentalDaysMonth ?? 0),
        ),
        rentalProfitDay,
        rentalProfitMonth,
      };
    });
  };

  const handleCustomerChange = useCallback(
    (clientOption: Nullable<ClientOption>) => {
      if (!clientOption?.value) {
        return;
      }
      const { id, value } = clientOption;
      setCustomerId(id ?? '');
      setCustomerName(value);
    },
    [],
  );

  const dataset =
    activeRentalHistorySource === RentalHistorySourceType.Customer
      ? rentalHistoryCustomer
      : rentalHistoryCompanyChain;

  return (
    <Dialog fullWidth maxWidth={'sm'} open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ minHeight: 200 }}>
        <ButtonGroup sx={{ ml: 1, my: 0.5 }}>
          {Object.values(RentalHistorySourceType).map(
            (option, index) => (
              <Button
                key={option + index}
                variant={
                  activeRentalHistorySource === option
                    ? 'contained'
                    : 'outlined'
                }
                color={
                  activeRentalHistorySource === option
                    ? 'primary'
                    : 'inherit'
                }
                onClick={() => setActiveRentalHistorySource(option)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  minWidth: 60,
                  padding: '2px 8px',
                }}
              >
                {option === RentalHistorySourceType.Customer
                  ? 'Asiakas'
                  : 'Yritysketju'}
              </Button>
            ),
          )}
        </ButtonGroup>
        {activeRentalHistorySource ===
          RentalHistorySourceType.Customer && (
          <Box sx={{ paddingTop: 2 }}>
            <CustomerSearchSelector
              customerName={customerName}
              customerId={customerId}
              onCustomerChange={handleCustomerChange}
              includeCustomerChains={false}
            />
          </Box>
        )}
        {activeRentalHistorySource ===
          RentalHistorySourceType.CompanyChain && (
          <Box sx={{ my: 2, ml: 1 }}>
            <Select
              size="small"
              value={companyChainId}
              onChange={(event: SelectChangeEvent<string>) => {
                setCompanyChainId(event.target.value);
                const selectedChain = companyChains.find(
                  (companyChain: any) =>
                    companyChain.companyId === event.target.value,
                );
                setCompanyChainName(selectedChain.name);
              }}
            >
              {[
                {
                  companyId: 'NotSelected',
                  name: 'Valitse yritysketju',
                },
              ]
                .concat(
                  companyChains?.sort((a: any, b: any) => {
                    return a.name.localeCompare(b.name, 'fi');
                  }),
                )

                .map((value: any, index) => {
                  return (
                    <MenuItem
                      key={value?.companyId + index}
                      value={value?.companyId}
                    >
                      {value?.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </Box>
        )}
        {isLoadingRentalHistoryJob && (
          <Box>
            <Typography>
              <CircularProgress
                color={'primary'}
                size={16}
                thickness={5}
              />{' '}
              Ladataan vuokrahistoriaa...
            </Typography>
            <Typography color="text.secondary">
              Huom. lataus voi joillakin asiakkailla kestää useita
              minuutteja
            </Typography>
          </Box>
        )}
        {activeRentalHistorySource ===
          RentalHistorySourceType.Customer && (
          <>
            {!isLoadingRentalHistoryJob &&
              customerId &&
              _.isEmpty(rentalHistoryCustomer?.data) && (
                <Typography>
                  Asiakkaalle {customerName} ei löytynyt
                  vuokrahistoriaa
                </Typography>
              )}
            {!isLoadingRentalHistoryJob &&
              !_.isEmpty(rentalHistoryCustomer?.data) && (
                <Typography>
                  {`Asiakkaan ${customerName} vuokrahistoriasta löytyi 
            ${Object.values(rentalHistoryCustomer?.data).length} vuokrattua
            tuotetta`}
                </Typography>
              )}
          </>
        )}
        {activeRentalHistorySource ===
          RentalHistorySourceType.CompanyChain && (
          <>
            {!isLoadingRentalHistory &&
              companyChainName &&
              _.isEmpty(rentalHistoryCompanyChain.data) && (
                <Typography>
                  Yritysketjulle {companyChainName} ei löytynyt
                  vuokrahistoriaa
                </Typography>
              )}
            {!isLoadingRentalHistory &&
              !_.isEmpty(rentalHistoryCompanyChain.data) && (
                <Typography>
                  {`Yritysketjun ${companyChainName} vuokrahistoriasta löytyi 
            ${Object.values(rentalHistoryCompanyChain.data).length} vuokrattua
            tuotetta`}
                </Typography>
              )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'end',
          px: 3,
          pt: 0,
          pb: 2,
        }}
      >
        <Button
          disabled={isLoadingRentalHistory}
          color={'inherit'}
          onClick={onCancel}
        >
          Peruuta
        </Button>
        <Button
          disabled={
            isLoadingRentalHistory ||
            isLoadingRentalHistoryJob ||
            isEmpty(dataset)
          }
          variant={'contained'}
          endIcon={
            loading && <CircularProgress thickness={4} size={16} />
          }
          onClick={() => {
            onConfirm(
              customerName,
              customerId,
              newStartDate,
              newEndDate,
              companyChainId,
              activeRentalHistorySource,
              formatPricingSheetRows(
                activeRentalHistorySource ===
                  RentalHistorySourceType.Customer
                  ? rentalHistoryCustomer
                  : rentalHistoryCompanyChain,
              ),
              companyChainName,
            );
          }}
        >
          Vaihda
        </Button>
      </DialogActions>
    </Dialog>
  );
};
